
import { useQuery } from '@vue/apollo-composable';
import type {
  Ticket,
  TicketCategory,
  TicketPaginator,
  TicketStatus,
  TicketPriority,
} from '@/types/custom-ticket-types';
import type { Maybe } from '@/types/composition-functions';
import { GET_TICKET_CATEGORIES, GET_TICKETS, GET_TICKET } from '@/graphql/Queries/tickets';
import { ON_TICKET_MESSAGE_SENT, ON_USER_NOTIFICATION } from '@/graphql/subscriptions/tickets';

export const useTicketQueries = () => {
  // Get Categories
  const useGetCategories = () => {
    const { result, loading, error, refetch } = useQuery<{ ticketCategories: TicketCategory[] }>(GET_TICKET_CATEGORIES);
    const categories = computed(() => result.value?.ticketCategories || []);
    return { categories, loading, error, refetch };
  };

  // Get Tickets List
  const useGetTickets = (variables: { status?: TicketStatus, priority?: TicketPriority, category_id?: string, page?: number } = {}) => {
    const { result, loading, error, refetch } = useQuery<{ tickets: TicketPaginator }>(GET_TICKETS, variables, {
      fetchPolicy: 'network-only', // Ensure freshness
    });
    const tickets = computed(() => result.value?.tickets.data || []);
    const paginator = computed(() => result.value?.tickets.paginatorInfo);
    return { tickets, paginator, loading, error, refetch };
  };

  // Get Single Ticket
  const useGetTicket = (id: string) => {
    const { result, loading, error, refetch, onResult } = useQuery<{ ticket: Ticket }>(GET_TICKET, { id }, {
      enabled: !!id,
      fetchPolicy: 'network-only',
    });
    const ticket = computed(() => result.value?.ticket);
    return { ticket, loading, error, refetch, onResult };
  };

  return {
    useGetCategories,
    useGetTickets,
    useGetTicket,
  };
};
